import classNames from "classnames";
import { ImageLoaderProps } from "next/image";
import { forwardRef, useMemo } from "react";

import styles from "./optimized-image.module.scss";

type Props = JSX.IntrinsicElements["img"] & {
  src: string;
  sizes?: string;
  priority?: boolean;
  isWideSize?: boolean;
  loader?: (props: ImageLoaderProps) => string;
};

export default forwardRef<HTMLImageElement, Props>(function OptimizedImage(props: Props, ref) {
  const { src, alt, className, priority, isWideSize, loader } = props;
  const blankPixel = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

  const sizes = useMemo(() => {
    return props.sizes || `(${isWideSize ? "max" : "min"}-width: 900px) 0vw, 100vw`;
  }, [isWideSize, props.sizes]);

  const srcSet = useMemo(() => {
    const sizesSet = isWideSize ? [1080, 1200, 1920, 2048, 3840] : [640, 750, 828];
    return sizesSet
      .map((size) => {
        return loader
          ? `${loader({ src, width: size })} ${size}w`
          : `/_next/image/?url=${encodeURIComponent(src)}&q=80&w=${size}&av=v1 ${size}w`;
      })
      .join(",");
  }, [src, isWideSize, loader]);

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      className={classNames(className, styles.fill)}
      alt={alt}
      srcSet={`${blankPixel} 1w, ${srcSet}`}
      sizes={sizes}
      ref={ref}
      // @ts-expect-error - This react version types don't include fetchpriority, remove this comment if upgraded to React 19+
      fetchpriority={priority ? "high" : "low"}
      loading={priority ? "eager" : "lazy"}
    />
  );
});
